import { AlarmingEventTrigger } from '@va/types/events';
import { PageTypeEnum } from '@va/types/website';
import { isWixApp } from '@va/util/helpers';

export enum FunnelLevelTypes {
  WebsitesPageURL = 'website_page',
  CustomURLPattern = 'url_pattern',
  CustomEvents = 'custom_event',
  ECommerceEvents = 'ecom_event',
  AlarmingBehaviorEvents = 'alarming_event',
  OutgoingTraffic = 'outgoing_traffic',
  AutoEvents = 'auto_event',
}

export const FunnelLevelTypeNames = isWixApp()
  ? {
      [FunnelLevelTypes.WebsitesPageURL]: 'websitePage',
      [FunnelLevelTypes.CustomURLPattern]: 'urlPattern',
      [FunnelLevelTypes.AlarmingBehaviorEvents]: 'alarmingEvent',
      [FunnelLevelTypes.OutgoingTraffic]: 'outgoingTraffic',
      [FunnelLevelTypes.AutoEvents]: 'autoEvent',
    }
  : {
      [FunnelLevelTypes.WebsitesPageURL]: 'websitePage',
      [FunnelLevelTypes.CustomURLPattern]: 'urlPattern',
      [FunnelLevelTypes.CustomEvents]: 'customEvent',
      [FunnelLevelTypes.ECommerceEvents]: 'ecomEvent',
      [FunnelLevelTypes.AlarmingBehaviorEvents]: 'alarmingEvent',
      [FunnelLevelTypes.OutgoingTraffic]: 'outgoingTraffic',
      [FunnelLevelTypes.AutoEvents]: 'autoEvent',
    };

export const FunnelLevelKind = isWixApp()
  ? {
      [FunnelLevelTypes.WebsitesPageURL]: 'website_page',
      [FunnelLevelTypes.CustomURLPattern]: 'url_pattern',
      [FunnelLevelTypes.AlarmingBehaviorEvents]: 'alarming_event',
      [FunnelLevelTypes.OutgoingTraffic]: 'outgoing_traffic',
      [FunnelLevelTypes.AutoEvents]: 'auto_event',
    }
  : {
      [FunnelLevelTypes.WebsitesPageURL]: 'website_page',
      [FunnelLevelTypes.CustomURLPattern]: 'url_pattern',
      [FunnelLevelTypes.CustomEvents]: 'custom_event',
      [FunnelLevelTypes.ECommerceEvents]: 'ecom_event',
      [FunnelLevelTypes.AlarmingBehaviorEvents]: 'alarming_event',
      [FunnelLevelTypes.OutgoingTraffic]: 'outgoing_traffic',
      [FunnelLevelTypes.AutoEvents]: 'auto_event',
    };

export type FunnelLevel<T extends FunnelLevelTypes | any = any> = {
  name: string;
  kind: (typeof FunnelLevelKind)[keyof typeof FunnelLevelKind];
  data?: T extends FunnelLevelTypes ? FunnelLevelValues[T] : any;
};

export type FunnelLevelValues = {
  [FunnelLevelTypes.WebsitesPageURL]: {
    type?: PageTypeEnum;
    url: string;
  };
  [FunnelLevelTypes.AutoEvents]: {
    action: string;
    category: string;
    label: string;
    value: string;
    triggerURLs: string[];
  };
  [FunnelLevelTypes.AlarmingBehaviorEvents]: {
    trigger: AlarmingEventTrigger[];
  };
  [FunnelLevelTypes.CustomEvents]: {
    action?: string;
    category: string;
    label?: string;
    value?: string;
    fieldsObject?: string;
    triggerURLs?: string[];
  };
  [FunnelLevelTypes.CustomURLPattern]: {
    match: string;
    url: string;
  };
  [FunnelLevelTypes.ECommerceEvents]: {
    type: string[];
    triggerURLs?: string[];
  };
  [FunnelLevelTypes.OutgoingTraffic]: {
    url: string;
  };
};

export type FunnelLevelWithIdType<T extends FunnelLevelTypes | any = any> = FunnelLevel<T> & {
  id: string;
};

export type FunnelInfoValuesType = {
  name: string;
  status: FUNNEL_STATUS_ENUM;
  expectedConversionRate?: number | null;
  levels: FunnelLevelWithIdType[];
  levelsCount: number;
};

export type FunnelType<T extends FunnelLevelTypes | any = any> = {
  levels: FunnelLevel<T>[];
} & FunnelInfoValuesType;

export enum FUNNEL_STATUS_ENUM {
  DRAFT = 'draft',
  PUBLISHED = 'published',
}

export enum FormFieldNames {
  name = 'name',
  status = 'status',
  expectedConversionRate = 'expectedConversionRate',
  levels = 'levels',
}

export enum FunnelPageModeEnum {
  createFunnel = 'createFunnel',
  editFunnel = 'editFunnel',
}

export enum EcommerceEventTypes {
  AddToCart = 'add_to_cart',
  AddToWishlist = 'add_to_wishlist',
  BeginCheckout = 'begin_checkout',
  Purchase = 'purchase',
  RemoveFromCart = 'remove_from_cart',
  ViewCart = 'view_cart',
  ViewItem = 'view_item',
  ViewItemList = 'view_item_list',
  AddPaymentInfo = 'add_payment_info',
  AddShippingInfo = 'add_shipping_info',
  Refund = 'refund',
  SelectItem = 'select_item',
  SelectPromotion = 'select_promotion',
  ViewPromotion = 'view_promotion',
}

export const EcommerceEventNames = {
  [EcommerceEventTypes.AddToCart]: 'add_to_cart',
  [EcommerceEventTypes.AddToWishlist]: 'add_to_wishlist',
  [EcommerceEventTypes.BeginCheckout]: 'begin_checkout',
  [EcommerceEventTypes.Purchase]: 'purchase',
  [EcommerceEventTypes.RemoveFromCart]: 'remove_from_cart',
  [EcommerceEventTypes.ViewCart]: 'view_cart',
  [EcommerceEventTypes.ViewItem]: 'view_item',
  [EcommerceEventTypes.ViewItemList]: 'view_item_list',
  [EcommerceEventTypes.AddPaymentInfo]: 'add_payment_info',
  [EcommerceEventTypes.AddShippingInfo]: 'add_shipping_info',
  [EcommerceEventTypes.Refund]: 'refund',
  [EcommerceEventTypes.SelectItem]: 'select_item',
  [EcommerceEventTypes.SelectPromotion]: 'select_promotion',
  [EcommerceEventTypes.ViewPromotion]: 'view_promotion',
};

export type LevelConversionDataType = {
  totalVisitors: number;
  dropoutsVisitors: number;
  conversionRate: number;
  dropoutsRate: number;
};

export type FunnelDetails = {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  createdBy: string | null;
  levels: FunnelLevelWithIdType[];
  status: FUNNEL_STATUS_ENUM;
  expectedConversionRate?: number | null;
  visitorCountInTimeRange: number;
  visitorCountInPreviousTimeRange: number;
  conversionRate: number;
  previousConversionRate: number;
  currentConversion: number;
  previousConversion: number;
  visitorCount: number;
  levelsConversionData: LevelConversionDataType[];
  visitorLimit?: number | null;
  levelsCount?: number;
  conversionRatePercentageChange: number;
  visitorPercentageChange: number;
  conversionPercentageChange: number;
};

export enum funnelsSelectOptionsEnum {
  all = 0,
}
